/**
 * On-call & escalation — the roster the "Page on-call" step wakes, the order it wakes them
 * in, and the HOURS it is allowed to. Outside the window a page is held as a morning message
 * instead of ringing a phone at 2 AM (the caller's voicemail is captured either way).
 *
 * Every edit posts and the whole roster comes back, so two people editing never see a stale
 * ladder. Writes are gated on policy.write; without it the screen is read-only.
 */
import { useEffect, useState } from "react";
import { Badge, Button, Field, Icon, Notice, Panel, Toggle } from "../components/ui";
import { api, ApiError } from "../../lib/api.ts";
import type { OncallContact, OncallResponse } from "../../lib/api.ts";
import { useResource, usePermissions } from "../../lib/useApi.ts";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toHHMM = (m: number): string =>
  `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const fromHHMM = (s: string): number => {
  const parts = s.split(":");
  const h = Number.parseInt(parts[0] ?? "0", 10);
  const m = Number.parseInt(parts[1] ?? "0", 10);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

export function Oncall() {
  const query = useResource<OncallResponse>(["oncall"], () => api.oncall(), { staleTimeMs: 15_000 });

  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading on-call…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="On-call unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  return <OncallEditor data={query.data} reload={() => void query.refetch()} />;
}

function OncallEditor({ data, reload }: { data: OncallResponse; reload: () => void }) {
  const { can } = usePermissions();
  const mayWrite = can("policy.write");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async (tag: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(tag);
    setErr(null);
    try {
      await fn();
      reload();
    } catch (e) {
      setErr((e as ApiError | null)?.detail ?? "That did not save. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack">
      {!mayWrite && <Notice>You do not have permission to change on-call. Everything here is read-only.</Notice>}
      {err && <Notice tone="red">{err}</Notice>}

      <Roster data={data} mayWrite={mayWrite} busy={busy} run={run} />
      <Hours data={data} mayWrite={mayWrite} busy={busy} run={run} />
    </div>
  );
}

// --------------------------------------------------------------------------- roster

function Roster({
  data,
  mayWrite,
  busy,
  run,
}: {
  data: OncallResponse;
  mayWrite: boolean;
  busy: string | null;
  run: (tag: string, fn: () => Promise<unknown>) => Promise<void>;
}) {
  const contacts = data.contacts;
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [mobile, setMobile] = useState("");

  const move = (index: number, dir: -1 | 1): void => {
    const next = [...contacts];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j]!, next[index]!];
    void run("reorder", () => api.reorderOncall(next.map((c) => c.id)));
  };

  const add = (): void => {
    if (!name.trim() || !mobile.trim()) return;
    void run("add", async () => {
      await api.addOncallContact({ name: name.trim(), e164: mobile.trim(), role: role.trim() || undefined });
      setName("");
      setRole("");
      setMobile("");
    });
  };

  return (
    <Panel
      title="Who's on call"
      action={<Badge tone={contacts.some((c) => c.active) ? "green" : "amber"}>{contacts.filter((c) => c.active).length} on the ladder</Badge>}
    >
      <p className="muted" style={{ marginTop: 0 }}>
        The assistant pages these people in order — the first active person first, then the next if
        nobody answers. Turn someone off to take them out of tonight's rotation without deleting them.
      </p>

      {contacts.length === 0 ? (
        <p className="muted">No one is on call yet. Add your first tech or CSR below.</p>
      ) : (
        <ul className="oncall-list">
          {contacts.map((c, i) => (
            <ContactRow
              key={c.id}
              contact={c}
              index={i}
              count={contacts.length}
              mayWrite={mayWrite}
              busy={busy}
              onMove={move}
              onToggleActive={() => run(`active:${c.id}`, () => api.patchOncallContact(c.id, { active: !c.active }))}
              onToggleSms={() => run(`sms:${c.id}`, () => api.patchOncallContact(c.id, { smsOk: !c.smsOk }))}
              onRemove={() => run(`del:${c.id}`, () => api.deleteOncallContact(c.id))}
            />
          ))}
        </ul>
      )}

      {mayWrite && data.eligible.length > 0 && (
        <div className="oncall-eligible">
          <span className="eyebrow">Add from your team</span>
          <div className="oncall-eligible-list">
            {data.eligible.map((u) => (
              <button
                key={u.userId}
                type="button"
                className="oncall-eligible-btn"
                disabled={busy === `add-user:${u.userId}`}
                onClick={() => run(`add-user:${u.userId}`, () => api.addOncallContact({ name: u.name, e164: u.phone, userId: u.userId }))}
              >
                <Icon name="plus" size={13} /> {u.name} <span className="muted nd-mono">{u.phone}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {mayWrite && (
        <div className="oncall-add">
          <Field label="Name">
            <input value={name} placeholder="Dana Ruiz" onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Role (optional)">
            <input value={role} placeholder="Lead technician" onChange={(e) => setRole(e.target.value)} />
          </Field>
          <Field label="Mobile" hint="Where the page goes. +1 then the number.">
            <input value={mobile} placeholder="+13045551234" onChange={(e) => setMobile(e.target.value)} />
          </Field>
          <Button variant="primary" icon="plus" loading={busy === "add"} disabled={!name.trim() || !mobile.trim()} onClick={add}>
            Add to the ladder
          </Button>
        </div>
      )}
    </Panel>
  );
}

function ContactRow({
  contact,
  index,
  count,
  mayWrite,
  busy,
  onMove,
  onToggleActive,
  onToggleSms,
  onRemove,
}: {
  contact: OncallContact;
  index: number;
  count: number;
  mayWrite: boolean;
  busy: string | null;
  onMove: (index: number, dir: -1 | 1) => void;
  onToggleActive: () => void;
  onToggleSms: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="oncall-row" data-off={contact.active ? undefined : "true"}>
      <div className="oncall-ord">
        <span className="oncall-seq">{index + 1}</span>
        {mayWrite && (
          <div className="oncall-move">
            <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => onMove(index, -1)}>
              <Icon name="down" size={14} className="oncall-flip" />
            </button>
            <button type="button" aria-label="Move down" disabled={index === count - 1} onClick={() => onMove(index, 1)}>
              <Icon name="down" size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="oncall-who">
        <span className="oncall-name">{contact.name}</span>
        {contact.role && <span className="muted"> · {contact.role}</span>}
        <div className="oncall-num nd-mono">{contact.e164}</div>
      </div>

      <Badge tone="neutral">{contact.smsOk ? "Text" : "Call"}</Badge>

      {mayWrite ? (
        <>
          <Toggle
            label={contact.active ? "On call" : "Off"}
            checked={contact.active}
            disabled={busy === `active:${contact.id}`}
            onChange={onToggleActive}
          />
          <button type="button" className="oncall-secondary" onClick={onToggleSms} disabled={busy === `sms:${contact.id}`}>
            {contact.smsOk ? "Switch to call" : "Switch to text"}
          </button>
          <button type="button" className="oncall-remove" aria-label={`Remove ${contact.name}`} onClick={onRemove} disabled={busy === `del:${contact.id}`}>
            <Icon name="trash" size={15} />
          </button>
        </>
      ) : (
        <Badge tone={contact.active ? "green" : "amber"}>{contact.active ? "On call" : "Off"}</Badge>
      )}
    </li>
  );
}

// --------------------------------------------------------------------------- hours

function Hours({
  data,
  mayWrite,
  busy,
  run,
}: {
  data: OncallResponse;
  mayWrite: boolean;
  busy: string | null;
  run: (tag: string, fn: () => Promise<unknown>) => Promise<void>;
}) {
  const { hours } = data.policy;
  const [alwaysOn, setAlwaysOn] = useState(hours.alwaysOn);
  const [start, setStart] = useState(hours.startMinute);
  const [end, setEnd] = useState(hours.endMinute);
  const [days, setDays] = useState<number[]>(hours.days);

  // Reset the draft when the server sends a new version (another editor, or our own save).
  useEffect(() => {
    setAlwaysOn(hours.alwaysOn);
    setStart(hours.startMinute);
    setEnd(hours.endMinute);
    setDays(hours.days);
  }, [hours.alwaysOn, hours.startMinute, hours.endMinute, hours.days]);

  const dirty =
    alwaysOn !== hours.alwaysOn ||
    start !== hours.startMinute ||
    end !== hours.endMinute ||
    days.slice().sort().join() !== hours.days.slice().sort().join();

  const save = (): void =>
    void run("hours", () => api.patchOncallHours({ alwaysOn, startMinute: start, endMinute: end, days }));

  return (
    <Panel
      title="On-call hours"
      action={
        mayWrite ? (
          <Button variant="primary" loading={busy === "hours"} disabled={!dirty} onClick={save}>
            Save hours
          </Button>
        ) : undefined
      }
    >
      <Toggle
        label="On call around the clock"
        description="Every after-hours call that needs a person pages the on-call ladder, any time of night."
        checked={alwaysOn}
        disabled={!mayWrite}
        onChange={() => setAlwaysOn((v) => !v)}
      />

      {!alwaysOn && (
        <div className="oncall-hours">
          <Field label="On call from">
            <input type="time" value={toHHMM(start)} disabled={!mayWrite} onChange={(e) => setStart(fromHHMM(e.target.value))} />
          </Field>
          <Field label="until" hint="An end time before the start means the window runs overnight.">
            <input type="time" value={toHHMM(end)} disabled={!mayWrite} onChange={(e) => setEnd(fromHHMM(e.target.value))} />
          </Field>
          <div className="oncall-days">
            {DAY_LABELS.map((label, d) => (
              <button
                key={label}
                type="button"
                className="oncall-day"
                aria-pressed={days.includes(d)}
                data-on={days.includes(d) ? "true" : undefined}
                disabled={!mayWrite}
                onClick={() => setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]))}
              >
                {label}
              </button>
            ))}
          </div>
          <Notice>
            Outside these hours a call still records a voicemail — the caller is never turned away — but
            nobody is paged until morning, when it's waiting in Escalations. Emergencies (fire, a downed
            line) always page, whatever the clock says.
          </Notice>
        </div>
      )}
    </Panel>
  );
}

export default Oncall;
